import React, { useState } from 'react';
import { ADMIN_ADJUSTMENT_TYPES, ADMIN_ADJUSTMENT_TREATMENTS } from '../utils/constants';
import DayPicker from './DayPicker';

const TYPE_LABEL = ADMIN_ADJUSTMENT_TYPES.reduce((m, t) => { m[t.value] = t.label; return m; }, {});

const fieldCls =
  'rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-[13px] focus:border-brand-500 focus:ring-brand-500';

// A rate this far off the employee's own day rate is a deliberate decision rather
// than a typo, so we ask for the reason rather than silently accepting it. This is
// the "why £166?" that otherwise costs a phone call to the client.
const RATE_VARIANCE_TOLERANCE = 0.005; // 0.5%

function formatPct(fraction) {
  const pct = fraction * 100;
  const rounded = Math.abs(pct) < 10 ? pct.toFixed(1) : pct.toFixed(0);
  return `${pct > 0 ? '+' : ''}${rounded}%`;
}

// Per-employee manual run-sheet adjustments. Lines added here are persisted on the
// submission's processing_checklist (admin-only — never touches the client's data)
// and folded into the run-sheet totals so the tool can reconcile with BrightPay.
//
// Two ways to enter one:
//   Amount     a flat figure, as before.
//   Days × rate  pick the dates on a calendar and give a daily rate. The total is
//                derived, and the dates and rate are kept alongside it so the
//                number can be explained months later without reconstructing it.
//
//   employeeId  who the line is for
//   items       array of { type, treatment?, amount, note, days?, daily_rate? }
//   dayRate     the employee's own day rate, for comparison (null if not derivable)
//   dayRateBasis  how that rate was worked out, shown as a tooltip
//   onAdd       (employeeId, adjustment) => Promise
//   onRemove    (employeeId, index) => Promise
export default function AdminAdjustmentEditor({
  employeeId, items = [], dayRate = null, dayRateBasis = null, onAdd, onRemove, formatCurrency,
}) {
  const [type, setType] = useState('pilon');
  const [treatment, setTreatment] = useState('gross_add');
  const [mode, setMode] = useState('amount'); // 'amount' | 'days'
  const [amount, setAmount] = useState('');
  const [days, setDays] = useState([]);
  const [dailyRate, setDailyRate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOther = type === 'other';
  const isDaysMode = mode === 'days';

  const parsedRate = parseFloat(dailyRate);
  const rateIsValid = Number.isFinite(parsedRate) && parsedRate > 0;
  const dayTotal = rateIsValid && days.length > 0
    ? Math.round(days.length * parsedRate * 100) / 100
    : null;

  // How far the entered rate sits from the employee's own day rate. This is the
  // bit that turns "£166" from a mystery into "their day rate plus 20%".
  const variance = rateIsValid && dayRate > 0 ? (parsedRate - dayRate) / dayRate : null;
  const rateDiffers = variance !== null && Math.abs(variance) > RATE_VARIANCE_TOLERANCE;

  const effectiveAmount = isDaysMode ? dayTotal : parseFloat(amount);
  const canSubmit = Number.isFinite(effectiveAmount) && effectiveAmount > 0
    && (!isDaysMode || !rateDiffers || note.trim().length > 0);

  const reset = () => {
    setAmount('');
    setDays([]);
    setDailyRate('');
    setNote('');
    setError('');
  };

  const submit = async () => {
    if (!Number.isFinite(effectiveAmount) || effectiveAmount <= 0) return;
    if (isDaysMode && rateDiffers && !note.trim()) {
      setError('This rate differs from their day rate — please say why.');
      return;
    }
    const adj = { type, amount: effectiveAmount.toFixed(2), note: note.trim() || null };
    if (isOther) adj.treatment = treatment;
    if (isDaysMode) {
      adj.days = days;
      adj.daily_rate = parsedRate.toFixed(2);
      // Recorded so the comparison still makes sense later, even if the employee's
      // salary changes after the fact.
      adj.reference_day_rate = dayRate !== null ? Number(dayRate).toFixed(2) : null;
    }
    setSaving(true);
    setError('');
    try {
      await onAdd(employeeId, adj);
      reset();
    } finally {
      setSaving(false);
    }
  };

  const describeItem = (it) => {
    const dayCount = Array.isArray(it.days) ? it.days.length : null;
    const rate = parseFloat(it.daily_rate);
    if (dayCount && Number.isFinite(rate)) {
      return `${dayCount} day${dayCount === 1 ? '' : 's'} × ${formatCurrency ? formatCurrency(rate) : `£${rate.toFixed(2)}`}`;
    }
    return null;
  };

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-gray-300 dark:border-gray-600">
      <div className="text-gray-500 uppercase tracking-wider text-[12px] font-semibold mb-1">Admin adjustments</div>
      <p className="text-[12px] text-gray-500 mb-2">
        Manual lines for bespoke pay (PILON, enhanced parental top-up, final-pay items…). They appear in the
        breakdown above and are folded into the run-sheet totals so the tool reconciles with BrightPay — they
        don&apos;t change the client&apos;s submission.
      </p>

      {items.length > 0 && (
        <ul className="space-y-1 mb-2">
          {items.map((it, i) => {
            const derivation = describeItem(it);
            return (
              <li key={i} className="flex justify-between items-baseline gap-2">
                <span className="text-gray-700 dark:text-gray-200">
                  <span className="text-brand-700 mr-1" title="Admin-added">✎</span>
                  {TYPE_LABEL[it.type] || 'Adjustment'}
                  {derivation && (
                    <span className="ml-1 text-[12px] text-gray-500 tabular">— {derivation}</span>
                  )}
                  {it.note && <span className="block text-[12px] text-gray-500 italic mt-0.5">{it.note}</span>}
                  {Array.isArray(it.days) && it.days.length > 0 && (
                    <span className="block text-[11px] text-gray-400 mt-0.5">
                      {it.days.map(d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })).join(', ')}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-ink dark:text-gray-100 font-medium tabular">
                    {formatCurrency ? formatCurrency(parseFloat(it.amount)) : it.amount}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemove(employeeId, i)}
                    className="text-gray-400 hover:text-rose-600 text-[16px] leading-none px-1"
                    aria-label="Remove adjustment"
                    title="Remove"
                  >
                    &times;
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* How the figure is entered. Flat amount stays the default — the day-rate
          form is for the "pay them £X a day for these dates" case. */}
      <div className="inline-flex rounded-md ring-1 ring-gray-200 dark:ring-gray-600 overflow-hidden mb-2">
        {[
          { value: 'amount', label: 'Amount' },
          { value: 'days', label: 'Days × rate' },
        ].map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => { setMode(m.value); setError(''); }}
            className={`px-3 py-1 text-[12px] font-medium transition-colors ${
              mode === m.value
                ? 'bg-brand-600 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-[11px] text-gray-500">
          Type
          <select value={type} onChange={(e) => setType(e.target.value)} className={`mt-0.5 ${fieldCls}`}>
            {ADMIN_ADJUSTMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        {isOther && (
          <label className="flex flex-col text-[11px] text-gray-500">
            Treatment
            <select value={treatment} onChange={(e) => setTreatment(e.target.value)} className={`mt-0.5 ${fieldCls}`}>
              {ADMIN_ADJUSTMENT_TREATMENTS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
        )}

        {isDaysMode ? (
          <label className="flex flex-col text-[11px] text-gray-500">
            Daily rate (£)
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={dailyRate}
              onChange={(e) => setDailyRate(e.target.value)}
              placeholder={dayRate !== null ? Number(dayRate).toFixed(2) : '0.00'}
              className={`mt-0.5 w-28 ${fieldCls}`}
            />
          </label>
        ) : (
          <label className="flex flex-col text-[11px] text-gray-500">
            Amount (£)
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={`mt-0.5 w-28 ${fieldCls}`}
            />
          </label>
        )}

        <label className="flex flex-col text-[11px] text-gray-500 flex-1 min-w-[140px]">
          {isDaysMode && rateDiffers ? 'Why this rate? (required)' : 'Note (optional)'}
          <input
            type="text"
            value={note}
            onChange={(e) => { setNote(e.target.value); setError(''); }}
            placeholder={isDaysMode && rateDiffers ? 'e.g. client confirmed 20% uplift' : 'Context for the processor'}
            className={`mt-0.5 ${isDaysMode && rateDiffers && !note.trim() ? 'border-amber-400' : ''} ${fieldCls}`}
          />
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={saving || !canSubmit}
          className="px-3 py-2 rounded-md text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>

      {isDaysMode && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,260px)_1fr] items-start">
          <div>
            <p className="text-[11px] text-gray-500 mb-1">Which days?</p>
            <DayPicker value={days} onChange={setDays} />
          </div>
          <div className="text-[12.5px] space-y-2">
            {dayRate !== null && (
              <p className="text-gray-500" title={dayRateBasis || undefined}>
                Their day rate is{' '}
                <span className="font-semibold text-ink dark:text-gray-100 tabular">
                  {formatCurrency ? formatCurrency(dayRate) : `£${Number(dayRate).toFixed(2)}`}
                </span>
                {dayRateBasis && <span className="block text-[11px] text-gray-400 mt-0.5">{dayRateBasis}</span>}
              </p>
            )}
            {rateIsValid && variance !== null && (
              <p className={rateDiffers ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-emerald-700 dark:text-emerald-400'}>
                {rateDiffers
                  ? `You've entered ${formatCurrency ? formatCurrency(parsedRate) : `£${parsedRate.toFixed(2)}`} — ${formatPct(variance)} vs their day rate.`
                  : 'Matches their day rate.'}
              </p>
            )}
            {days.length > 0 && (
              <p className="text-gray-600 dark:text-gray-300">
                {days.length} day{days.length === 1 ? '' : 's'} selected
                {dayTotal !== null && (
                  <>
                    {' — '}
                    <span className="font-semibold text-ink dark:text-gray-100 tabular">
                      {days.length} × {formatCurrency ? formatCurrency(parsedRate) : `£${parsedRate.toFixed(2)}`}
                      {' = '}
                      {formatCurrency ? formatCurrency(dayTotal) : `£${dayTotal.toFixed(2)}`}
                    </span>
                  </>
                )}
              </p>
            )}
            {days.length === 0 && (
              <p className="text-gray-400">Pick the dates on the calendar — the count and total follow from them.</p>
            )}
            {error && <p className="text-rose-600">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
